import React from 'react';
import { PencilIcon } from '@heroicons/react/outline';
import Button from './Button';
import Tooltip from './Tooltip';
import Banner from './Banner';
import useInlineEditCommit from './useInlineEditCommit';
import { cn } from './cn';

/** Double-click editing with a keyboard/touch action and outside-click commit.
 * The caller owns the value, editor and save operation. Rich content may contain
 * links or controls, so it is never wrapped in a native button. */
export default function InlineEdit({
  defaultComponent,
  editComponent,
  showEdit,
  setShowEdit,
  handleSubmitEdit = () => {},
  componentName,
  actions,
  showEditButton = true,
  stackActionsOnMobile = false,
  revealOnHover = false,
  className,
}) {
  const { boundaryRef, error: saveError } = useInlineEditCommit({
    editing: showEdit,
    onCommit: () => handleSubmitEdit(componentName),
    onClose: () => setShowEdit(false),
  });
  return (
    <div ref={boundaryRef} className={cn(revealOnHover && 'bun-hover-actions', className)}>
      {saveError && <Banner variant="danger">Couldn't save your changes. Try again.</Banner>}
      {showEdit ? (
        editComponent
      ) : (
        <div className={cn('flex min-w-0 items-start gap-1', stackActionsOnMobile && 'flex-wrap sm:flex-nowrap')}>
          <div className={cn('min-w-0 flex-1', stackActionsOnMobile && 'order-last w-full flex-none sm:order-none sm:w-auto sm:flex-1')} onDoubleClick={() => setShowEdit(true)}>
            {defaultComponent}
          </div>
          {(showEditButton || actions) && <div className={cn('flex shrink-0 items-start gap-1', stackActionsOnMobile && 'ml-auto')}>
            {showEditButton && <Tooltip label="Double click to edit" portal>
              <Button
                variant="ghost"
                size="sm"
                iconOnly
                revealOnHover={revealOnHover}
                aria-label={`Edit ${componentName}`}
                onClick={() => setShowEdit(true)}
              >
                <PencilIcon className="h-4 w-4" />
              </Button>
            </Tooltip>}
            {actions}
          </div>}
        </div>
      )}
    </div>
  );
}
